import {
  getTravDetailsFromMemory,
  setTravDetailsInMemory,
} from "@tm/insurance/utils";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  IPrimaryContact,
  ISecondaryContactV2,
} from "./InsuranceTravellerDetailV2/InsuranceTravellerDetailV2";

export interface ITravellerDetailsFormV2 {
  travellerData: ISecondaryContactV2[];
  primaryContactData: IPrimaryContact;
  tncs: boolean[];
}

export interface ITravellerDetailsFromMemory {
  travellerDetails?: ISecondaryContactV2[];
  primaryContactDetails?: IPrimaryContact;
  countryCode?: string;
}

interface TravelDetailsV2ContextType {
  // State
  travellerDataFromStore: ITravellerDetailsFromMemory;
  formValues: ITravellerDetailsFormV2 | null;
  errorState: {
    firstErrorIndex: number;
    hasErrors: boolean;
    errorMessage: string | null;
  };

  // Functions
  saveFormValuesOnBlur: (index?: number) => void;
  updateTravellerData: (data: ISecondaryContactV2[]) => void;
  updatePrimaryContactData: (data: IPrimaryContact) => void;
  getTravellerDetailsFromMemory: () => ITravellerDetailsFromMemory;
  setTravellerDetailsInMemory: (data: ITravellerDetailsFromMemory) => void;
  setErrorState: (errorState: {
    firstErrorIndex?: number;
    hasErrors?: boolean;
    errorMessage?: string | null;
  }) => void;
  clearErrors: () => void;
  focusOnError: (index: number) => void;
}

const TravelDetailsV2Context = createContext<
  TravelDetailsV2ContextType | undefined
>(undefined);

interface TravelDetailsV2ContextProviderProps {
  children: ReactNode;
  initialFormValues?: ITravellerDetailsFormV2;
}

/**
 * TravelDetailsV2ContextProvider provides traveller data and related functions
 * to child components. This context centralizes the management of traveller
 * details data and provides a clean API for components to interact with it.
 *
 * Usage example:
 * ```tsx
 * const MyComponent = () => {
 *   const {
 *     travellerDataFromStore,
 *     saveFormValuesOnBlur,
 *     updateTravellerData
 *   } = useTravelDetailsV2Context();
 *
 *   const handleSave = () => {
 *     saveFormValuesOnBlur();
 *   };
 *
 *   return <div>...</div>;
 * };
 * ```
 */
export const TravelDetailsV2ContextProvider: React.FC<
  TravelDetailsV2ContextProviderProps
> = ({ children, initialFormValues }) => {
  const [formValues, setFormValues] = useState<ITravellerDetailsFormV2 | null>(
    initialFormValues || null
  );

  // Get initial data from memory (after client-side hydration)
  const [travellerDataFromStore, setTravellerDataFromStore] =
    useState<ITravellerDetailsFromMemory>({});

  // Error state management
  const [errorState, setErrorStateInternal] = useState({
    firstErrorIndex: -1,
    hasErrors: false,
    errorMessage: null as string | null,
  });

  // Read session storage only after client-side hydration
  useEffect(() => {
    const dataFromStorage = getTravDetailsFromMemory();
    setTravellerDataFromStore(dataFromStorage);
  }, []);

  const getTravellerDetailsFromMemory = useCallback(() => {
    return getTravDetailsFromMemory();
  }, []);

  const setTravellerDetailsInMemory = useCallback(
    (data: ITravellerDetailsFromMemory) => {
      setTravDetailsInMemory(data);
    },
    []
  );

  const saveFormValuesOnBlur = useCallback(
    (index?: number) => {
      if (formValues) {
        const currentDataFromStore = getTravDetailsFromMemory();
        setTravellerDetailsInMemory({
          ...currentDataFromStore,
          travellerDetails: formValues.travellerData,
          primaryContactDetails: formValues.primaryContactData,
        });
      }
    },
    [formValues, setTravellerDetailsInMemory]
  );

  const updateTravellerData = useCallback(
    (data: ISecondaryContactV2[]) => {
      if (formValues) {
        setFormValues({
          ...formValues,
          travellerData: data,
        });
      }
    },
    [formValues]
  );

  const updatePrimaryContactData = useCallback(
    (data: IPrimaryContact) => {
      if (formValues) {
        const currentDataFromStore = getTravDetailsFromMemory();
        setFormValues({
          ...formValues,
          primaryContactData: data,
        });
        setTravellerDetailsInMemory({
          ...currentDataFromStore,
          primaryContactDetails: data,
        });
      }
    },
    [formValues, setTravellerDetailsInMemory]
  );

  // Error handling functions
  const setErrorState = useCallback(
    (newErrorState: {
      firstErrorIndex?: number;
      hasErrors?: boolean;
      errorMessage?: string | null;
    }) => {
      setErrorStateInternal((prev) => ({
        ...prev,
        ...newErrorState,
      }));
    },
    []
  );

  const clearErrors = useCallback(() => {
    setErrorStateInternal({
      firstErrorIndex: -1,
      hasErrors: false,
      errorMessage: null,
    });
  }, []);

  const focusOnError = useCallback(
    (index: number) => {
      setErrorState({
        firstErrorIndex: index,
        hasErrors: true,
      });

      // Add smooth scroll animation to error element
      setTimeout(() => {
        const errorElement = document.querySelector(
          `[data-traveller-index="${index}"]`
        );
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 100);
    },
    [setErrorState]
  );

  const contextValue: TravelDetailsV2ContextType = {
    travellerDataFromStore,
    formValues,
    errorState,
    saveFormValuesOnBlur,
    updateTravellerData,
    updatePrimaryContactData,
    getTravellerDetailsFromMemory,
    setTravellerDetailsInMemory,
    setErrorState,
    clearErrors,
    focusOnError,
  };

  return (
    <TravelDetailsV2Context.Provider value={contextValue}>
      {children}
    </TravelDetailsV2Context.Provider>
  );
};

/**
 * Hook to access the TravelDetailsV2Context
 * @returns TravelDetailsV2ContextType
 * @throws Error if used outside of TravelDetailsV2ContextProvider
 */
export const useTravelDetailsV2Context = () => {
  const context = useContext(TravelDetailsV2Context);
  if (context === undefined) {
    throw new Error(
      "useTravelDetailsV2Context must be used within a TravelDetailsV2ContextProvider"
    );
  }
  return context;
};
